import { Maybe } from '@aibees/shared/definition';
import {
  validateLeads,
  validateMeetings,
  validateTicks,
} from '@aibees/shared/features/price-and-plan/components/plan-detail/components/how-credits-work/credit-calculator/components/dialog-left-side/validateHelpers';
import { useBoolean } from '@aibees/shared/hooks';
import { createContext, useCallback, useContext, useState } from 'react';
import { useCalculateAiPersonalityCredits } from '../hooks/use-calculate-ai-personality-credits';
import { useCalculateContactCredits } from '../hooks/use-calculate-contact-credits';
import { useCalculateTotalCredits } from '../hooks/use-calculate-total-credits';
import {
  PlanDetailContextData as ContextData,
  DialogContextProps as ContextProps,
} from './types';

const context = createContext<ContextData>({} as ContextData);

export const { Provider } = context;

export const usePlanDetailContext = () => useContext(context);

export const PlanDetailProvider = ({ children }: ContextProps) => {
  const [leads, setLeads] = useState<Maybe<number>>(0);
  const [meetings, setMeetings] = useState<Maybe<number>>(0);

  const [contactCredits, setContactCredits] = useState<Maybe<number>>(0);
  const [aiPersonalityCredits, setAiPersonalityCredits] =
    useState<Maybe<number>>(0);
  const [totalCredits, setTotalCredits] = useState<Maybe<number>>(0);

  const [leadsError, setLeadsError] = useState('');
  const [meetingsError, setMeetingsError] = useState('');
  const [ticksError, setTicksError] = useState('');

  const validWorkEmail = useBoolean();
  const mobilePhoneNumber = useBoolean();
  const fullProfileEnrichment = useBoolean();

  const contact = useCalculateContactCredits(
    leads,
    validWorkEmail,
    mobilePhoneNumber,
    fullProfileEnrichment
  );
  const personality = useCalculateAiPersonalityCredits(meetings);
  const total = useCalculateTotalCredits(contact, personality);

  const calculate = useCallback(() => {
    const leadsErrorMsg = validateLeads(leads);
    const meetingsErrorMsg = validateMeetings(meetings);
    const ticksErrorMsg = validateTicks(
      validWorkEmail.value,
      mobilePhoneNumber.value,
      fullProfileEnrichment.value
    );

    if (leadsErrorMsg) setLeadsError(leadsErrorMsg);
    else setLeadsError('');
    if (meetingsErrorMsg) setMeetingsError(meetingsErrorMsg);
    else setMeetingsError('');
    if (ticksErrorMsg) setTicksError(ticksErrorMsg);
    else setTicksError('');

    if (!leadsErrorMsg && !meetingsErrorMsg && !ticksErrorMsg) {
      setContactCredits(contact);
      setAiPersonalityCredits(personality);
      setTotalCredits(total);
    }
  }, [
    leads,
    meetings,
    validWorkEmail,
    mobilePhoneNumber,
    fullProfileEnrichment,
  ]);

  const reset = () => {
    setLeads(0);
    setMeetings(0);
    setContactCredits(0);
    setAiPersonalityCredits(0);
    setTotalCredits(0);
    setLeadsError('');
    setMeetingsError('');
    setTicksError('');
    validWorkEmail.onFalse();
    mobilePhoneNumber.onFalse();
    fullProfileEnrichment.onFalse();
  };

  return (
    <Provider
      value={{
        leads,
        setLeads,
        meetings,
        setMeetings,
        validWorkEmail,
        mobilePhoneNumber,
        fullProfileEnrichment,
        contactCredits,
        aiPersonalityCredits,
        totalCredits,
        leadsError,
        setLeadsError,
        meetingsError,
        setMeetingsError,
        ticksError,
        setTicksError,
        calculate,
        reset,
      }}
    >
      {children}
    </Provider>
  );
};
